import {Component, ComponentRef, ViewChild} from '@angular/core';
import {HostBaseComponent} from "../../model/hostBase/host-base.component";
import {ActivatedRoute, ParamMap, Router} from "@angular/router";
import {map, Observable, of, switchMap, tap, zip} from "rxjs";
import {ModalComponent} from "../../../utils/modal/modal.component";
import {NativeServiceApiProvider} from "../../../services/native-api-provider.service";
import {catchError} from "rxjs/operators";
import {ConfirmDialogService} from "../../../utils/services/confirm-dialog.service";
import {TranslateService} from "@ngx-translate/core";
import {OffcanvasMenuService} from "../../../services/offcanvas-menu.service";
import {ShortcutService} from "../../services/shortcut.service";
import {QuestionService} from "../../services/api/impl/rest/question.service";
import {CspaRestV3} from "../../services/v3/cspa-rest-v3";
import {SessionInfoService} from "../../../services/session-info.service";
import {DictComponent} from "../../components/exercises/dict/dict.component";
import { FontSizeService } from 'src/app/services/font-size.service';
import {AuthorizationServiceProvider} from "../../../auth_profile/services/authorization-service.provider";
import {AuthorizedUserDetailsService} from "../../../auth_profile/services/api/authorization.api";
import {AnswerHolderBase, V3ExamSession, V3ExamSessionPart, V3ExerciseSessionQuestion} from "../../model/v3/cspa";

type ExamHostSessionData = {
  session: V3ExamSession;
  parts: V3ExamSessionPart[];
  currentPart: V3ExamSessionPart | null;
  questions: V3ExerciseSessionQuestion[];
}

@Component({
    selector: 'app-exam-host',
    templateUrl: './exam-host.component.html',
    styleUrls: ['../../model/hostBase/host-base.component.scss'],
    standalone: false
})
export class ExamHostComponent extends HostBaseComponent<V3ExamSession> {
  isExam: boolean = false
  examPartId: number
  redirectCountdown: number = null;
  timeLimitIntervalId: any
  countdownIntervalId: any
  timeLimit: number = null;
  isTooltipVisible: boolean = false;
  private personId?: number;
  private examParts: V3ExamSessionPart[] = [];
  private currentPart?: V3ExamSessionPart;
  private sessionQuestions: V3ExerciseSessionQuestion[] = [];

  @ViewChild('examModal', {static: true}) examModal: ModalComponent;
  @ViewChild('timeLimitModal', {static: true}) timeLimitModal: ModalComponent;
  @ViewChild('lockedExamPartSessionModal', {static: true}) lockedExamPartSessionModal: ModalComponent

  constructor(
    route: ActivatedRoute,
    confirmDialogService: ConfirmDialogService,
    translateService: TranslateService,
    offcanvasMenuService: OffcanvasMenuService,
    shortcut: ShortcutService,
    questionService: QuestionService,
    progressInfoService: SessionInfoService,
    private provider: NativeServiceApiProvider,
    private auth: AuthorizationServiceProvider,
    private router: Router,
    fontSizeService: FontSizeService
  ) {
    super(
      route,
      confirmDialogService,
      translateService,
      offcanvasMenuService,
      shortcut,
      questionService,
      progressInfoService,
      fontSizeService
    )
  }

  override ngOnInit(): void {
    super.ngOnInit()
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.examModal.hide()
    this.timeLimitModal.hide()
    clearInterval(this.timeLimitIntervalId)
    clearInterval(this.countdownIntervalId)
  }

  retrieveFromParamMap(params: ParamMap) {
    this.examPartId = +params.get("sessionPartId") - 1;
    this.isExam = !!params.get("sessionPartId");
  }

  getQuestions() {
    return this.sessionQuestions
  }

  getExerciseName() {
    return this.session?.examName
  }

  getSession() {
    return zip(this.provider.cspaV3(), this.auth.getAuthDetailsService()).pipe(
      switchMap(([api, authDetailsApi]) =>
        this.getPersonId(authDetailsApi).pipe(
          switchMap(personId => this.loadSessionData(api, personId)),
        ),
      ),
      tap(sessionData => this.applyLoadedSessionData(sessionData)),
      map(sessionData => sessionData.session),
    )
  }

  private loadSessionData(api: CspaRestV3, personId: number) {
    return this.loadSessionWithParts(api, personId).pipe(
      switchMap(({session, parts}) => {
        const currentPart = this.resolveCurrentPart(session, parts);
        if (!currentPart) {
          return of({
            session,
            parts,
            currentPart: null,
            questions: [],
          } as ExamHostSessionData);
        }
        return this.ensurePartIsOpen(api, personId, currentPart).pipe(
          switchMap(openPart => this.loadQuestionsForOpenPart(api, personId, session, parts, openPart)),
        );
      }),
    );
  }

  private loadSessionWithParts(api: CspaRestV3, personId: number) {
    return zip(
      api.getExamSession(personId, this.uuId),
      api.findExamSessionsParts(personId, this.uuId),
    ).pipe(
      map(([session, parts]) => ({session, parts})),
    );
  }

  private ensurePartIsOpen(api: CspaRestV3, personId: number, part: V3ExamSessionPart) {
    if (part.status === 'CREATED') {
      return api.updatePartStatus(personId, this.uuId, part.num, 'OPEN');
    }
    return of(part);
  }

  private loadQuestionsForOpenPart(
    api: CspaRestV3,
    personId: number,
    session: V3ExamSession,
    parts: V3ExamSessionPart[],
    openPart: V3ExamSessionPart,
  ) {
    return api.getPartQuestions(personId, this.uuId, openPart.num).pipe(
      map((questions: V3ExerciseSessionQuestion[]) => ({
        session,
        parts: parts.map(part => part.num === openPart.num ? openPart : part),
        currentPart: openPart,
        questions,
      } as ExamHostSessionData)),
    );
  }

  private applyLoadedSessionData(sessionData: ExamHostSessionData) {
    this.session = sessionData.session
    this.examParts = sessionData.parts
    this.currentPart = sessionData.currentPart ?? undefined
    this.sessionQuestions = sessionData.questions
    this.question = this.getQuestion()
    this.question && this.setupQuestionTimings(this.question)
    this.goToQuestion(this.questionNb)
    this.startCountdown()
  }

  goToQuestion(index: number | string) {
    if (+index > this.getQuestions().length) {
      this.examModal.show()
    } else {
      this.onLoadQuestion(this.getQuestions()[+index - 1])
      this.router.navigate(
        [`../${index}`],
        {
          relativeTo: this.route,
          queryParamsHandling: 'merge'
        }
      ).then()
    }
  }

  finishSession() {
    return this.withApiAndPersonId((api, personId) =>
      this.finishCurrentPartAndAdvanceExam(api, personId).pipe(
        catchError(err => this.handleLockedSessionError(err)),
        switchMap(_ => api.getExamSession(personId, this.uuId)),
      ),
    )
  }

  goBackFromExam() {
    this.lockedExamPartSessionModal.hide()
    this.router.navigate(
      ['../../../../../../../'], {
        relativeTo: this.route,
        queryParamsHandling: 'merge'
      }
    ).then()
  }

  startCountdown() {
    const limit = this.currentPart?.timeLimitDate
    if (!limit) return
    if (new Date(limit).getTime() <= new Date().getTime()) {
      this.lockedExamPartSessionModal.show()
    }
    if (!this.timeLimitIntervalId) {
      this.timeLimitIntervalId = setInterval(() => {
        this.timeLimit = new Date(limit).getTime() - new Date().getTime()
        if (this.timeLimit <= 0) {
          this.showTimeLimitModal()
          this.clearTimeLimitTimer()
        }
      }, 1000)
    }
  }

  showTimeLimitModal() {
    if (this.lockedExamPartSessionModal.isShown()) {
      return
    }

    this.redirectCountdown = 5
    this.timeLimitModal.show(false)

    this.postAnswerAndFinishSession()

    if (!this.countdownIntervalId) {
      this.countdownIntervalId = setInterval(() => {
        if (this.redirectCountdown === 0) {
          this.timeLimitModal.hide()
          this.goToNextPartInTimeLimitModal()
        }
        this.redirectCountdown -= 1
      }, 1000)
    }
  }

  goBack() {
    this.finishSession().pipe(
      catchError(_ => of(null))
    ).subscribe(_ => {
      const redirectPath = this.questionNb !== 'finish'
        ? '../../../finish'
        : '../../../../../../../'
      this.navigateToNextPart(redirectPath)
    });
  }

  getExamName() {
    return this.session?.examName
  }

  getExamPartName() {
    return this.currentPart?.partName
  }

  getCurrentPartNr() {
    return this.examParts.findIndex(it => it.path == this.currentPart?.path) + 1
  }

  isSampleExam() {
    const sampleTag = this.session?.examName?.split(' ')[1]
    return sampleTag === '0'
  }

  isExamFinished() {
    return (this.session?.partsDone ?? 0) + 1 >= this.examParts.length
  }

  goToNextPart(withFinishSession: boolean) {
    this.examModal.hide();
    this.timeLimitModal.hide();
    this.clearTimeLimitTimer();
    const path = this.isExamFinished() ? '../../../finish' : `../../${+this.examPartId + 2}/1`

    if (withFinishSession) {
      this.finishSession().subscribe(_ => this.navigateToNextPart(path))
      return
    }
    this.navigateToNextPart(path)
  }

  postAnswer() {
    return this.withApiAndPersonId((api, personId) =>
      this.submitCurrentQuestionAnswer(api, personId),
    )
  }

  postAnswerAndGoToQuestion() {
    this.postAnswer().subscribe(_ => this.goToQuestion(+this.questionNb + 1))
  }

  postAnswerAndFinishSession() {
    this.postAnswer().pipe(
      catchError(err => this.handleLockedSessionError(err)),
      switchMap(_ => this.finishSession()),
    ).subscribe()
  }

  postAnswerAndGoToNextPart() {
    this.postAnswer().subscribe(_ => this.goToNextPart(true))
  }

  prepareAudioProgressEmitter(componentRef: ComponentRef<DictComponent>) {
    componentRef.instance.audioProgressEmitter.subscribe((progress: number) => {
      if (progress === -1) {
        return
      }
      this.progressInfoService.setSessionInfo({
        questionNb: this.questionNb,
        session: this.session,
        questionType: this.getQuestionType(),
        questionCount: this.getQuestions()?.length,
        progressPercent: progress
      })
    })
  }

  isAudio() {
    return this.question?.questionDef['@type'] === "V3Audio"
  }

  showTooltip(show: boolean) {
    this.isTooltipVisible = show;
  }

  goToNextPartInTimeLimitModal() {
    this.clearCountdownTimer()
    this.goToNextPart(false)
  }

  navigateToNextPart(path: string) {
    this.router.navigate(
      [path],
      {
        relativeTo: this.route,
        queryParamsHandling: 'merge'
      }
    ).then()
  }

  private getPersonId(authDetailsApi: AuthorizedUserDetailsService) {
    if (this.personId != null) {
      return of(this.personId);
    }
    return authDetailsApi.getSelfPersonId().pipe(
      tap(personId => this.personId = personId),
    );
  }

  private resolvePersonId() {
    if (this.personId != null) {
      return of(this.personId);
    }
    return this.auth.getAuthDetailsService().pipe(
      switchMap(authDetailsApi => this.getPersonId(authDetailsApi)),
    );
  }

  private resolveCurrentPart(session: V3ExamSession, parts: V3ExamSessionPart[]) {
    if (parts.length === 0) {
      return null;
    }
    if (this.examPartId >= 0 && this.examPartId < parts.length) {
      return parts[this.examPartId];
    }
    if (session.currentPart?.num != null) {
      return parts.find(part => part.num === session.currentPart.num) ?? parts[0];
    }
    if (session.currentPart?.path) {
      return parts.find(part => part.path === session.currentPart.path) ?? parts[0];
    }
    return parts[0];
  }

  private getCurrentPartNum() {
    return this.currentPart?.num ?? this.examPartId + 1;
  }

  private getCurrentQuestionNum() {
    return +this.questionNb - 1
  }

  private clearTimeLimitTimer() {
    clearInterval(this.timeLimitIntervalId)
    this.timeLimitIntervalId = null
  }

  private clearCountdownTimer() {
    clearInterval(this.countdownIntervalId)
    this.countdownIntervalId = null
  }

  private withApiAndPersonId<T>(action: (api: CspaRestV3, personId: number) => Observable<T>) {
    return this.provider.cspaV3().pipe(
      switchMap(api =>
        this.resolvePersonId().pipe(
          switchMap(personId => action(api, personId)),
        ),
      ),
    )
  }

  private finishCurrentPartAndAdvanceExam(api: CspaRestV3, personId: number) {
    const currentPartNum = this.getCurrentPartNum()
    const nextPartNum = currentPartNum + 1
    const hasNextPart = this.examParts.some(part => part.num === nextPartNum)

    return api.updatePartStatus(personId, this.uuId, currentPartNum, 'FINISHED').pipe(
      switchMap(() => {
        if (hasNextPart) {
          return api.updatePartStatus(personId, this.uuId, nextPartNum, 'OPEN')
        }
        return api.updateExamSessionStatus(personId, this.uuId, 'FINISHED')
      }),
    )
  }

  private submitCurrentQuestionAnswer(api: CspaRestV3, personId: number) {
    const answerHolder = this.toAnswerHolder(this.question)
    if (!answerHolder) {
      return of(null)
    }
    return api.submitPartQuestionAnswer(
      personId,
      this.uuId,
      this.getCurrentPartNum(),
      this.getCurrentQuestionNum(),
      answerHolder,
    )
  }

  private handleLockedSessionError(err: any) {
    if (err.status === 408 || err.status === 423) {
      this.lockedExamPartSessionModal.show()
    }
    return of(null)
  }

  private toAnswerHolder(question: V3ExerciseSessionQuestion): AnswerHolderBase | null {
    const questionType = question?.questionDef?.['@type']
    if (question?.questionDef?.answerDef?.answerHolder != null) {
      return {
        ...(question.questionDef.answerDef.answerHolder as any),
        '@type': (question.questionDef.answerDef.answerHolder as any)?.['@type'] ?? this.resolveAnswerHolderType(questionType),
      } as AnswerHolderBase;
    }
    return null;
  }

  private resolveAnswerHolderType(questionType?: string): string {
    const supportedTypes = new Set([
      'V3Correction',
      'V3Dictation',
      'V3FillMulti',
      'V3Jumble',
      'V3Images',
      'V3Qa',
      'V3Audio',
    ])

    if (!questionType || !supportedTypes.has(questionType)) {
      throw new Error(`Unsupported question type: ${questionType}`)
    }
    return `${questionType}AnswerHolder`
  }
}
